import {
  Box,
  CircularProgress,
  Button as MuiButton,
  TextField,
} from '@mui/material';
import BasicTable from 'components/Form/Table';
import ModalDeclinarProposta from 'components/Modal/ModalDeclinarProposta';
import ContainerAuthenticated from 'containers/Authenticated';
import { CoreContext } from 'context/CoreContext';
import moment from 'moment';
import 'moment/locale/pt-br';
import { useCallback, useContext, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { Read } from 'services/financing-proposals';
import { TableInfo, Title } from 'ui/styled';
import { marcadorDiferente } from 'utils';

const STATUS_DECLINED = 'declinadas';
const REJECTABLE_STATUSES = [
  'pendente',
  'aprovado',
  'a_formalizar',
  'em_analise',
  'autorizado',
];
const EXCLUDED_SEARCH_STATUSES = ['a pagar', 'concluido'];

export default function DeclinarPropostas() {
  const { user, filterExpression } = useContext(CoreContext);
  const [isLoading, setIsLoading] = useState(false);
  const [proposals, setProposals] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProposal, setSelectedProposal] = useState(null);
  moment.locale('pt-br');
  const fetchProposalById = async (proposalId) => {
    setIsLoading(true);
    try {
      if (!proposalId) {
        toast.warn('Por favor, insira um ID para buscar.');
        setProposals([]);
        return;
      }
      //const params = new ('?filters[id]=2626');
      const result = await Read('?filters[id]=' + proposalId);
      //console.log(result);
      if (result && Array.isArray(result.results) && !result.error) {
        const filteredResults = result.results.filter(
          (p) => !EXCLUDED_SEARCH_STATUSES.includes(p.status?.toLowerCase())
        );
        if (filteredResults.length > 0) {
          setProposals(filteredResults);
        } else {
          setProposals([]);
          toast.warn(
            'Nenhuma proposta encontrada com este ID ou status disponível.'
          );
        }
      } else {
        setProposals([]);
        toast.error(
          'Erro ao carregar propostas: resposta inválida do servidor.'
        );
      }
    } catch (error) {
      console.error('Erro ao buscar propostas:', error);
      toast.error('Erro de conexão. Tente novamente.');
      setProposals([]);
    } finally {
      setIsLoading(false);
    }
  };
  const handleSearch = () => {
    const proposalId = searchQuery.trim();
    if (!proposalId) {
      toast.warn('Por favor, insira um ID para buscar.');
      return;
    }
    if (isNaN(Number(proposalId))) {
      toast.warn('A busca só é permitida pelo ID numérico da proposta.');
      return;
    }
    fetchProposalById(proposalId);
  };
  const handleSelectProposal = useCallback(
    (proposal) => {
      if (!user?.administrador) {
        return toast.error('Você não tem permissão para realizar esta ação.');
      }
      if (!REJECTABLE_STATUSES.includes(proposal.status.toLowerCase())) {
        return toast.warn(
          'Esta proposta não pode ser recusada no seu status atual.'
        );
      }
      setSelectedProposal(proposal);
    },
    [user]
  );
  const handleCloseDeclineModal = () => {
    setSelectedProposal(null);
  };
  const parseCurrency = (value) => {
    if (value == null || isNaN(Number(value))) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };
  const capitalize = (text) => {
    if (!text) return '';
    return text
      .toLowerCase()
      .split(' ')
      .map((s) => s.charAt(0).toUpperCase() + s.substring(1))
      .join(' ');
  };
  const parseDate = (date) => {
    const parsed = moment(date);
    return parsed.isValid() ? parsed.format('L') : 'N/A';
  };
  const calculateDuration = (startDate) => {
    if (!startDate || !moment(startDate).isValid()) return 'N/A';
    const start = moment(startDate);
    const now = moment();
    const duration = moment.duration(now.diff(start));
    const days = Math.floor(duration.asDays());
    return `${days} dias`;
  };
  const rows = useMemo(() => {
    return proposals.map((item) => {
      return {
        ...item,
        id: item?.id ?? 'N/A',
        date: parseDate(item?.createdAt),
        time: calculateDuration(item?.createdAt),
        cod_proposta_externa: item?.cod_proposta_externa,
        distributor: item?.distribuidor?.trade_name,
        integrator: item?.integrador?.nome_fantasia,
        customer: item?.informacoes_cliente?.nome_completo || 'N/A',
        value: parseCurrency(
          item?.project?.total_cost ||
            item?.informacoes_projeto?.custo_total_projeto
        ),
        status: capitalize(item?.status),
        originalProposal: item,
        curadoria_analise: item?.curadoria_analise, // Assumindo que este campo está no backend
      };
    });
  }, [proposals]);

  const columns = useMemo(() => {
    const baseColumns = [
      {
        title: 'Id',
        ref: 'id',
        renderCell: ({ row }) => {
          // Condição: Se o campo 'curadoria_analise' for 'Analisado'
          const isAnalisado = row?.curadoria_analise === 'Analisado';
          const id = row?.id;
          if (isAnalisado) {
            return marcadorDiferente(id, '!');
          }
          return id;
        },
      },
      { title: 'Data', ref: 'date' },
      { title: 'Tempo', ref: 'time' },
      { title: 'Proposta', ref: 'cod_proposta_externa' },
      { title: 'Distribuidora', ref: 'distributor' },
      { title: 'Integrador', ref: 'integrator' },
      { title: 'Cliente', ref: 'customer' },
      { title: 'Valor', ref: 'value' },
      {
        title: 'Status',
        renderCell: ({ row }) => (
          <TableInfo
            color={
              row.status.toLowerCase() === STATUS_DECLINED ? 'red' : 'blue'
            }
          >
            {row.status}
          </TableInfo>
        ),
      },
    ];
    if (user?.administrador) {
      baseColumns.push({
        title: 'Declinar',
        renderCell: ({ row }) => (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              width: '100%',
            }}
          >
            <MuiButton
              variant="contained"
              color="blue"
              nospace
              smal={1}
              onClick={() => handleSelectProposal(row.originalProposal)}
            >
              Declinar
            </MuiButton>
          </Box>
        ),
      });
    }
    return baseColumns;
  }, [user, handleSelectProposal]);

  return (
    <ContainerAuthenticated>
      <Title>Declinar Propostas</Title>
      <Box sx={{ display: 'flex', gap: 2, mb: 4, alignItems: 'flex-end' }}>
        <TextField
          label="Buscar Proposta (ID)"
          variant="outlined"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleSearch();
          }}
          placeholder="Digite o ID da proposta"
          sx={{ flexGrow: 1 }}
          InputLabelProps={{ shrink: true }}
          aria-label="Buscar proposta por ID"
        />
        <MuiButton
          variant="contained"
          onClick={handleSearch}
          disabled={isLoading}
          sx={{ height: '56px' }}
          color="blue"
        >
          {isLoading ? <CircularProgress size={24} /> : 'Procurar'}
        </MuiButton>
      </Box>
      {proposals.length > 0 ? (
        <BasicTable
          loading={isLoading}
          columns={columns}
          rows={rows?.filter(filterExpression)}
          pagination
        />
      ) : (
        <Box sx={{ textAlign: 'center', py: 2 }}>
          <p>Realize uma busca para exibir resultados.</p>
        </Box>
      )}
      {selectedProposal && (
        <ModalDeclinarProposta
          proposalId={selectedProposal.id}
          codPropostaExterna={
            selectedProposal?.proposal?.external_id ||
            selectedProposal?.informacoes_proposta?.id_proposta_externa ||
            selectedProposal.id
          }
          onClose={handleCloseDeclineModal}
          reloadContent={() => fetchProposalById(selectedProposal.id)}
        />
      )}
    </ContainerAuthenticated>
  );
}
